import { useCallback, useEffect, useRef, useState } from 'react';
import Video, { ConnectOptions, LocalTrack, Room } from 'twilio-video';

import { isMobile } from 'utils';
import { Callback } from 'types';

export function useRoom(localTracks: LocalTrack[], onError: Callback, options?: ConnectOptions) {
  const [room, setRoom] = useState<Room | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const optionsRef = useRef(options);
  const localTracksRef = useRef(localTracks);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    localTracksRef.current = localTracks;
  }, [localTracks]);

  const connect = useCallback(
    (token: string) => {
      setIsConnecting(true);

      const isGroupCall = localStorage.getItem('groupCallsEnabled') === 'true';

      const finalOptions: ConnectOptions = {
        ...optionsRef.current,
        tracks: localTracksRef.current,
        ...(isGroupCall && {
          dominantSpeaker: true,
          networkQuality: {
            local: 1,
            remote: 1,
          },
        }),
      };

      return Video.connect(token, finalOptions).then(
        (newRoom) => {
          setRoom(newRoom);
          const disconnect = () => newRoom.disconnect();

          // Multiple hooks/components attach listeners to the same room events.
          // Disable EventEmitter listener limit warnings for this shared emitter.
          newRoom.setMaxListeners(0);

          newRoom.once('disconnected', (room, error) => {
            setTimeout(() => setRoom(null));
            window.removeEventListener('beforeunload', disconnect);

            if (isMobile) {
              window.removeEventListener('pagehide', disconnect);
            }
          });

          newRoom.localParticipant.videoTracks.forEach((publication) => {
            publication.setPriority('low');
          });

          setIsConnecting(false);

          window.addEventListener('beforeunload', disconnect);

          if (isMobile) {
            window.addEventListener('pagehide', disconnect);
          }
        },
        (error) => {
          onError(error);
          setIsConnecting(false);
        },
      );
    },
    [localTracks, onError],
  );

  return { room, isConnecting, connect };
}
